import { Component, Element, h, Host, Prop } from '@stencil/core';
import type { LsItem } from '../../utils/linescale.models';
import { Axis, axisTop, ScaleBand, scaleBand, scaleLinear, ScaleLinear, select, Selection } from 'd3';
import { LsLabel, wrap } from '../../utils/label';

@Component({
  tag: 'ls-linescale',
  styleUrl: 'linescale-component.css',
  shadow: true,
})
export class LinescaleComponent {
  @Element() host: HTMLElement;

  // @Prop() data!: W_LinescaleRender;
  @Prop() scaleText: string[] = ['Good', 'Great', 'Terrific'];
  @Prop() items: LsItem[] = [];

  @Prop() isQuestion?: boolean = false;
  @Prop() skipUrl: string;
  @Prop() limit: number;
  @Prop() vMargin: number = 0;
  @Prop() tickMark: { value: number; label: string };

  // Raw SVG element ref
  svgContainerEl!: HTMLElement;

  // private _isQuestion: boolean = false;
  readonly ratedItems: { [id: string]: number } = {};
  readonly results: any[];
  text: string;
  prelim: string;

  readonly margin = {
    top: 75,
    right: 25,
    // right: 0,
    bottom: 25,
    // left: 0
    left: 25,
  };

  fillColor: string = '#3f51b5';

  readonly fullWidth: number = 700;
  readonly fullHeight: number = 650;
  readonly height: number = this.fullHeight - this.margin.bottom - this.margin.top;
  readonly axisYOffset: number = 100;
  readonly axisXOffset: number = 38;
  readonly axisHeight: number = 8;
  readonly strokeWidth: number = 4;
  readonly trianglePoints = {
    pointA: [16 + this.linescaleWidth, this.axisYOffset + this.axisHeight / 2],
    pointB: [this.linescaleWidth, this.axisYOffset + this.axisHeight + 2],
    pointC: [this.linescaleWidth, this.axisYOffset - 2],
  };

  private readonly _items: LsLabel[] = [];

  svg: Selection<SVGElement, any, any, any>;
  x: ScaleLinear<number, number> = scaleLinear()
    // .domain([724, 0])
    .domain([743, 19])
    .range([0, this.linescaleWidth - this.axisXOffset])
    .clamp(true);
  xLabel: ScaleBand<any>;
  xAxisCall: Axis<string>;
  chartBox: Selection<HTMLElement, any, any, any>;
  // private line: Selection<SVGRectElement, string, any, any>;

  /**
   * Score value (currently, may change to coordinate value)
   * UPDATE #1: MUST BE coordinate value due to reversed domain
   */
  private threshhold: number = 150;
  private lowestRating: LsLabel;

  get linescaleWidth(): number {
    return this.fullWidth - this.margin.left - this.margin.right;
  }

  get arrowWidth(): number {
    return this.axisXOffset;
  }

  private xPos(value: number): number {
    return this.x(value) + this.axisXOffset;
  }

  private drawLinescale(scaleText: string[], height: number): void {
    // const ctrlThis = this;

    this.xLabel = scaleBand()
      .domain(scaleText)
      .range([0, this.linescaleWidth - this.axisXOffset]);
    // .paddingInner(0.5);
    // .paddingOuter(0);
    this.xAxisCall = axisTop(this.xLabel).ticks(scaleText.length).tickSize(8).tickSizeOuter(0);

    this.svg = this.chartBox
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      // Always hardcoded for vector coordinates
      .attr('viewBox', `0 0 ${this.fullWidth} 158`);

    const axis: Selection<SVGGElement, string, any, any> = this.svg
      .append('g')
      .attr('class', 'x axis')
      .call(this.xAxisCall)
      // .attr('height', 100)
      // .attr('width', this.linescaleWidth - this.arrowWidth)
      .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);

    // this.line = this.svg
    this.svg
      .append('rect')
      .attr('width', this.linescaleWidth - this.axisXOffset)
      .attr('height', this.axisHeight)
      .attr('fill', this.fillColor)
      // .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);
      .attr('transform', `translate(${this.axisXOffset}, ${this.axisYOffset})`);

    // const clickLine: Selection<SVGRectElement, string, any, any> = this.svg
    this.svg
      .append('rect')
      // .attr('width', this.linescaleWidth - this.axisXOffset)
      .attr('width', this.linescaleWidth - this.arrowWidth)
      .attr('height', this.axisHeight + 15)
      .attr('fill', 'transparent')
      .attr('class', 'ls-line')
      .attr('transform', `translate(${this.arrowWidth}, ${this.axisYOffset - 5})`);

    const { pointA, pointB, pointC } = this.trianglePoints;
    this.svg
      .append('polygon')
      .attr('points', `${pointA[0]},${pointA[1]} ${pointB[0]},${pointB[1]} ${pointC[0]},${pointC[1]}`)
      .attr('style', `fill: ${this.fillColor}; stroke: ${this.fillColor}; stroke-linejoin:round; stroke-width: ${this.strokeWidth}`);

    // clickLine.on('click', function clickStatus() {
    //   if (!ctrlThis._isQuestion) return;
    //   else if (ctrlThis.ratedItems[ctrlThis.data.id]) return;
    //   const myMouse: number[] = pointer(this);
    //   const value: number = Math.floor(ctrlThis.x.invert(myMouse[0]));

    //   // TODO: This should be called for standalone linescales. However NgSwitch directive in LsScreen component seems to rebuild component on expression change even for same value. Investigate
    //   if (ctrlThis.data.rs_id) {
    //     // ctrlThis.addLabel(ctrlThis.lsData.label, value, ctrlThis.threshhold);
    //   }

    //   // TODO: Re-enable to draw scored labels and emitter
    //   // ctrlThis.renderLabels();
    //   // ctrlThis.ratedItems[ctrlThis.data.id] = value;
    //   // ctrlThis.scored.emit({
    //   //   [ctrlThis.data.questionId]: value,
    //   // });
    // });

    // This does the wrapping for scale guides
    axis.selectAll('.tick text').call(wrap, this.xLabel.bandwidth());
    // axis.selectAll('.x g.tick').attr('class', 'lift-ticks')
    axis.selectAll('text').attr('class', 'axis-label');
    axis.selectAll('line').attr('class', 'tick-mark').attr('stroke', this.fillColor);
    axis.selectAll('path').attr('stroke', this.fillColor);
  }

  private addRating(text: string, value: number, threshhold: number, isAnchor: boolean = false): void {
    const label = new LsLabel(
      value,
      text,
      {
        axis: {
          yPos: this.axisYOffset + this.axisHeight,
          xPos: this.axisXOffset,
        },
        linescaleWidth: this.linescaleWidth,
        height: this.height,
        xScale: this.x,
        svg: this.svg,
        marginX: this.margin.left,
        allLabels: this._items,
        threshhold,
        vBoxWidth: this.fullWidth,
        marginY: this.vMargin,
      },
      isAnchor,
    );
    this._items.push(label);

    // Keep track of lowest rating
    if (this.lowestRating) {
      if (value < this.lowestRating.score) this.lowestRating = label;
    } else this.lowestRating = label;
  }

  /**
   * Returns labels in rendering order
   * @param labels List of labels
   */
  private sortLabels(): LsLabel[] {
    return this._items.sort((a, b) => a.score - b.score);
  }

  private renderLabels(): void {
    if (!this._items.length) return;

    // Sorts the items into descending score order and replaces the items in the SAME array object with new sorted array items
    // TODO: Possibly move this out to the initializer 'onChanges' routine
    this._items.splice(0, this._items.length, ...this.sortLabels());
    // console.log(this._items);

    // If more than one rating loop through and render from lowest score to highest to allow propper alignment of text and resizing of svg element
    if (this._items.length > 1) {
      let itemA: LsLabel, itemB: LsLabel;

      for (let x = this._items.length - 1; x > 0; x--) {
        itemB = this._items[x];
        itemA = this._items[x - 1];
        // console.log(itemB.text);

        itemB.render();
        itemA.boxY = itemB.boxY + itemB.boxHeight + 8;
      }
    }
    // Render the highest rated item which is not included the loop. Will also render in the case of only a single rating in list
    const highestRated: LsLabel = this._items[0];
    highestRated.render();
    this.resizeSVG();
  }

  /**
   * Resizes the main SVG container to fit the items drawn
   */
  private resizeSVG(): void {
    const first: LsLabel = this._items[0];
    const nextHeight = first.boxY + first.boxHeight + 16;

    this.svg.attr('height', nextHeight).attr('viewBox', `0 0 700 ${nextHeight}`);
  }

  private drawScore(value: number, desc: number | string): void {
    this.svg
      .append('rect')
      .attr('x', this.xPos(value))
      .attr('y', this.axisYOffset - 5)
      .attr('height', 25)
      .attr('width', 3)
      .style('fill: blue;');
    this.svg
      .append('text')
      .attr('x', this.xPos(value))
      .attr('y', this.axisYOffset + this.axisHeight + 25)
      .text(desc);
  }

  render() {
    return (
      <Host>
        <sot />
        <div ref={el => (this.svgContainerEl = el)}></div>
      </Host>
    );
  }

  componentDidLoad() {
    // const svgEl = this.host.shadowRoot.querySelectorAll('svg')[0];
    // const container = this.host.shadowRoot.querySelectorAll('.svg-container')[0];
    const container = this.svgContainerEl;
    this.chartBox = select(container as HTMLElement);

    const height: number = this.margin.top + this.margin.bottom + this.axisHeight + 50;
    this.drawLinescale(this.scaleText, height);
    if (this.tickMark) {
      // TODO: Do you need the multi/divi...?
      this.drawScore(Math.floor(this.tickMark.value), this.tickMark.label);
    }

    // Override vertical margin between linescale rating labels
    if (this.vMargin) {
      // this.margin.bottom
    }

    if (this.items) {
      // Make a copy to not mutate component input
      let subset: LsItem[] = this.items.slice();
      // FIXME: Why is this not being used?
      // const myThings = subset.filter(t => t.value);
      // console.log('All length w Val: ', myThings.length);

      // Reverse the order to last rated to first
      const anchorItems = this.items.filter(item => item.is_anchor);
      if (this.limit && subset.length > this.limit) {
        const startPos = subset.length - this.limit;
        const endPos = subset.length;

        subset = subset.slice(startPos, endPos);
      }
      if (subset.length && anchorItems.length) {
        console.log('Should not be here...');
        let pos: number = 0;
        anchorItems.forEach(anchItem => {
          if (!subset.includes(anchItem)) {
            while (subset[pos].is_anchor && pos < subset.length - 1) {
              pos++;
            }
            subset[pos] = anchItem;
            pos++;
          }
        });
      }

      // Filter out any items with no values
      // TODO: Rework this. Feels redundant/out of place
      subset = subset.filter(item => item.value);

      // console.log('Items before: ', this._items.length);
      subset.forEach(item => this.addRating(item.label, item.value, this.threshhold, item.is_anchor));
      // console.log('After: ', this._items.length);

      this.renderLabels();

      /**
       * Resize the SVG element to accommodate rating heights
       */
      if (this._items.length) {
        this.resizeSVG();
      }
    }
  }
}
